import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  ChevronRight,
  ChevronDown,
  CheckCircle,
  Users,
  GraduationCap,
  Heart,
  Award,
  ArrowUp,
  FileText,
  Building,
  Globe,
  Code,
  Settings,
  Wrench,
  Cpu,
  Monitor,
  Loader,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

// Layout
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Common components
import Container from './components/common/Container';
import SectionHeading from './components/common/SectionHeading';

const GaloCareersPage = () => {
  // State management
  const [scrolled, setScrolled] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  
  // Form state management
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    areaOfInterest: '',
    experienceLevel: '',
    technicalSkills: [],
    resume: null,
    aboutYourself: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null
  });
  
  // Refs for scroll animations
  const cultureRef = useRef(null);
  const benefitsRef = useRef(null);
  const applicationRef = useRef(null);
  const teamRef = useRef(null);
  const faqRef = useRef(null);
  
  // Real team testimonials using actual team members
  const testimonials = [
    {
      id: 1,
      name: "Deni Mišlov",
      position: "CEO & Founder",
      years: "Lead Software Developer",
      image: "/img/deni-mislov-new.jpg",
      quote: "My vision for Galo Industries was to combine deep technical knowledge with exceptional project management skills, creating a team that truly understands implementation challenges, not just theoretical automation design."
    },
    {
      id: 2,
      name: "Đani Kurić",
      position: "Lead Software Developer",
      years: "Automation Engineer",
      image: "/img/dani-kuric-new.jpg",
      quote: "As an Electrical Engineer with a passion for technology, I've enjoyed developing control systems and automation software at Galo, ensuring that all solutions meet the highest quality standards."
    },
    {
      id: 3,
      name: "Roko Košta",
      position: "Software Developer",
      years: "Automation Engineer",
      image: "/img/roko-kosta-new.jpg",
      quote: "My background in mechanical engineering combined with software development gives me a unique perspective on problem-solving, constantly seeking new ways to optimize industrial processes."
    }
  ];
  
  // Company benefits - balanced approach
  const benefits = [
    {
      title: "Professional Growth",
      description: "Develop your skills in automation technologies through challenging projects and continuous learning.",
      icon: <GraduationCap className="w-5 h-5" />
    },
    {
      title: "International Projects",
      description: "Work on diverse projects with major clients in various industries.",
      icon: <Globe className="w-5 h-5" />
    },
    {
      title: "Work-Life Balance",
      description: "We value personal time and flexibility to ensure sustainable productivity and wellbeing.",
      icon: <Heart className="w-5 h-5" />
    },
    {
      title: "Collaborative Team",
      description: "Join a supportive team that shares knowledge and works together to solve challenges.",
      icon: <Users className="w-5 h-5" />
    },
    {
      title: "Competitive Package",
      description: "Enjoy attractive compensation with opportunities for performance recognition.",
      icon: <Award className="w-5 h-5" />
    },
    {
      title: "Hands-on Experience",
      description: "Gain practical experience working directly with cutting-edge technologies and systems.",
      icon: <Wrench className="w-5 h-5" />
    }
  ];
  
  // FAQ items - updated with more specific content
  const faqItems = [
    {
      question: "What backgrounds are you looking for?",
      answer: "We welcome professionals with backgrounds in electrical engineering, mechanical engineering, computer science, and related fields. Experience with automation systems, software development, or industrial processes is valuable but not always required."
    },
    {
      question: "Do you offer opportunities for travel?",
      answer: "Yes, many of our projects involve travel to client sites. These opportunities provide valuable experience working with different industrial environments and international teams."
    },
    {
      question: "What technologies do your teams work with?",
      answer: "Our teams work with various control systems, programming languages, and industrial technologies. We use different PLC platforms, develop custom software solutions, and implement various automation systems based on project requirements."
    },
    {
      question: "Do you offer internships for students?",
      answer: "Yes, we offer internship programs for students interested in gaining practical experience in our field. These internships provide real-world exposure working alongside our experienced team members on actual projects."
    }
  ];
  
  // Handle scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Handle animation on scroll with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    
    const refs = [cultureRef, benefitsRef, applicationRef, teamRef, faqRef];
    
    refs.forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });
    
    return () => {
      refs.forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);
  
  // Handle FAQ toggle
  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'technicalSkills') {
        setFormData(prev => ({
          ...prev,
          technicalSkills: checked 
            ? [...prev.technicalSkills, value]
            : prev.technicalSkills.filter(skill => skill !== value)
        }));
      }
    } else if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: e.target.files[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ isSubmitting: true, isSubmitted: false, error: null });

    try {
      // Convert file to base64 if present
      let resumeData = null;
      if (formData.resume) {
        try {
          const base64String = await fileToBase64(formData.resume);
          resumeData = {
            fileName: formData.resume.name,
            fileSize: formData.resume.size,
            fileType: formData.resume.type,
            fileContent: base64String, // This includes the data:mime;base64, prefix
            base64Data: base64String.split(',')[1] // Just the base64 content without prefix
          };
        } catch (fileError) {
          console.error('File processing error:', fileError);
          throw new Error('Failed to process uploaded file. Please try again.');
        }
      }

      // Prepare the structured data for the webhook
      const submissionData = {
        timestamp: new Date().toISOString(),
        applicant: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          fullName: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
          phone: formData.phone
        },
        application: {
          areaOfInterest: formData.areaOfInterest,
          experienceLevel: formData.experienceLevel,
          technicalSkills: formData.technicalSkills,
          aboutYourself: formData.aboutYourself,
          hasResume: !!formData.resume,
          resume: resumeData // Complete file data including base64 content
        },
        source: 'Galo Industries Careers Page',
        userAgent: navigator.userAgent
      };

      // Send to webhook
      const response = await fetch('https://hook.eu2.make.com/o7j1rt1yocxte03rbjvji4kifdom57xc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Success
      setFormStatus({ 
        isSubmitting: false, 
        isSubmitted: true, 
        error: null 
      });

      // Reset form after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        areaOfInterest: '',
        experienceLevel: '',
        technicalSkills: [],
        resume: null,
        aboutYourself: ''
      });

      // Reset file input
      const fileInput = document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus({ 
        isSubmitting: false, 
        isSubmitted: false, 
        error: error.message || 'Failed to submit application. Please try again.' 
      });
    }
  };

  return (
    <div className="font-sans antialiased overflow-x-hidden bg-white">
      {/* Enhanced SEO Metadata */}
      <Helmet>
        <title>Careers at Galo Industries | Join Our Team</title>
        <meta
          name="description"
          content="Join Galo Industries and be part of a dynamic team working on exciting automation projects. Discover career opportunities with us."
        />
        <meta name="keywords" content="careers, jobs, automation, industrial technologies, PLC programming, software development, Croatia" />
      </Helmet>

      {/* Header */}
      <Header current="careers" />
      
      {/* Careers Hero Section - Updated with more balanced messaging */}
      <section className="pt-24 pb-16 bg-gradient-to-r from-cyan-600 to-blue-700 text-white relative overflow-hidden">
        {/* Background decorations - simplified */}
        <div className="absolute inset-0 bg-[radial-gradient(circle,_rgba(255,255,255,0.1)_1px,_transparent_1px)] bg-[length:20px_20px]"></div>
        
        <Container className="relative z-10">
          {/* Breadcrumb */}
          <div className="mb-6">
            <div className="flex items-center text-blue-100 text-sm">
              <a href="/" className="hover:text-white transition-colors">Home</a>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-white font-medium">Careers</span>
            </div>
          </div>
          
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Join Our <span className="text-cyan-200">Team</span> of Specialists
            </h1>
            
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Be part of a dynamic team working on exciting projects. At Galo Industries, we value innovation, collaboration, and professional growth.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="bg-white/10 px-4 py-2 rounded-md border border-white/10 flex items-center">
                <Globe className="w-5 h-5 mr-2 text-cyan-200" />
                <span className="text-blue-100">International Projects</span>
              </div>
              <div className="bg-white/10 px-4 py-2 rounded-md border border-white/10 flex items-center">
                <Users className="w-5 h-5 mr-2 text-cyan-200" />
                <span className="text-blue-100">Collaborative Team</span>
              </div>
              <div className="bg-white/10 px-4 py-2 rounded-md border border-white/10 flex items-center">
                <GraduationCap className="w-5 h-5 mr-2 text-cyan-200" />
                <span className="text-blue-100">Continuous Learning</span>
              </div>
            </div>
            
            <a 
              href="#open-application"
              className="inline-flex items-center bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-3 rounded-md hover:shadow-lg transition-all duration-300 text-lg font-medium"
            >
              Apply Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </Container>
      </section>
      
      {/* Culture Section - More balanced approach */}
      <section className="py-16 bg-white" ref={cultureRef}>
        <Container>
          <SectionHeading
            eyebrow="Our Culture"
            title={<>A Culture of <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Innovation</span></>}
            description="Join a team that values excellence, collaboration, and continuous improvement"
            centered
          />
          
          <div className="max-w-5xl mx-auto mt-10 culture-content">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div className="relative">
                {/* Single impactful image */}
                <div className="rounded-lg overflow-hidden shadow-md">
                  <img 
                    src="/img/innovation.jpg" 
                    alt="Galo Industries team collaborating on a project" 
                    className="w-full aspect-video object-cover"
                  />
                  
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">Where Talent Meets Opportunity</h3>
                <p className="text-slate-600 mb-6">
                  At Galo Industries, we've created a dynamic work environment where talented professionals can thrive. Our team works on interesting projects for clients providing opportunities to develop skills and gain valuable experience in various sectors.
                </p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-start">
                    <div className="w-8 h-8 rounded-md bg-gradient-to-r from-cyan-500 to-blue-600 flex-shrink-0 flex items-center justify-center text-white mr-2">
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900 text-sm">Collaborative Approach</h4>
                      <p className="text-slate-600 text-xs">Work together on exciting projects</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-8 h-8 rounded-md bg-gradient-to-r from-cyan-500 to-blue-600 flex-shrink-0 flex items-center justify-center text-white mr-2">
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900 text-sm">Professional Development</h4>
                      <p className="text-slate-600 text-xs">Continuous learning opportunities</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-8 h-8 rounded-md bg-gradient-to-r from-cyan-500 to-blue-600 flex-shrink-0 flex items-center justify-center text-white mr-2">
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900 text-sm">Projects</h4>
                      <p className="text-slate-600 text-xs">Work with clients</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-8 h-8 rounded-md bg-gradient-to-r from-cyan-500 to-blue-600 flex-shrink-0 flex items-center justify-center text-white mr-2">
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900 text-sm">Creative Problem-Solving</h4>
                      <p className="text-slate-600 text-xs">Innovative approaches to challenges</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Benefits Section - Balanced approach */}
      <section className="py-16 bg-gray-50" ref={benefitsRef}>
        <Container>
          <SectionHeading
            eyebrow="Benefits & Perks"
            title={<>Why You'll Love <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Working Here</span></>}
            description="We prioritize your growth, wellbeing, and work-life balance"
            centered
          />
          
          <div className="max-w-4xl mx-auto mt-8 benefits-grid">
            <div className="grid md:grid-cols-3 gap-4">
              {benefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-md p-5 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 benefit-card"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-10 h-10 rounded-md bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white mb-3">
                    {benefit.icon}
                  </div>
                  
                  <h3 className="text-base font-bold text-slate-900 mb-1">{benefit.title}</h3>
                  <p className="text-slate-600 text-xs">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>
      
      {/* Team Testimonials - Using real team members */}
      <section className="py-16 bg-white" ref={teamRef}>
        <Container>
          <SectionHeading
            eyebrow="Our Team"
            title={<>Meet the <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">People</span></>}
            description="The voices behind Galo Industries"
            centered
          />
          
          <div className="max-w-4xl mx-auto mt-8 testimonials-grid">
            <div className="grid md:grid-cols-3 gap-4">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={testimonial.id}
                  className="bg-white rounded-md shadow-md overflow-hidden testimonial-card border border-gray-100"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="p-4">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden shadow-sm flex-shrink-0">
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-bold text-slate-900">{testimonial.name}</h3>
                        <div className="text-cyan-700 text-xs">{testimonial.position}</div>
                        <div className="text-slate-400 text-xs">{testimonial.years}</div>
                      </div>
                    </div>
                    
                    <p className="text-slate-600 text-xs italic leading-relaxed line-clamp-4">"{testimonial.quote}"</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>
      
      {/* Open Application Section - Updated with balanced language */}
      <section id="open-application" className="py-16 bg-gray-50" ref={applicationRef}>
        <Container>
          <SectionHeading
            eyebrow="Join Our Team"
            title={<>Open <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Application</span></>}
            description="Submit your application and we'll contact you when opportunities match your profile"
            centered
          />
          
          <div className="max-w-2xl mx-auto mt-8 application-content">
            {/* Success Message */}
            {formStatus.isSubmitted && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
                <div className="flex items-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-green-800">Application Submitted Successfully!</h3>
                    <p className="text-sm text-green-700 mt-1">
                      Thank you for your interest in joining our team. We'll review your application and contact you soon.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {formStatus.error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Submission Failed</h3>
                    <p className="text-sm text-red-700 mt-1">{formStatus.error}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-md shadow-md p-6">
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                    <input 
                      type="text" 
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="First name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
                    <input 
                      type="text" 
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="Last name"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="Email address"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="Phone number"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Area of Interest</label>
                    <select 
                      name="areaOfInterest"
                      value={formData.areaOfInterest}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option value="">Select your area of interest</option>
                      <option value="automation">Automation</option>
                      <option value="software">Software Development</option>
                      <option value="project">Project Management</option>
                      <option value="design">Design & Implementation</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Experience Level</label>
                    <select 
                      name="experienceLevel"
                      value={formData.experienceLevel}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option value="">Select experience level</option>
                      <option value="entry">Entry Level (0-2 years)</option>
                      <option value="mid">Mid-Level (3-5 years)</option>
                      <option value="senior">Senior Level (6+ years)</option>
                      <option value="lead">Lead/Management</option>
                      <option value="student">Student/Internship</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Technical Skills (if applicable)</label>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="programming" 
                        name="technicalSkills"
                        value="programming"
                        checked={formData.technicalSkills.includes('programming')}
                        onChange={handleInputChange}
                        className="mr-2" 
                      />
                      <label htmlFor="programming" className="text-sm text-slate-700">Programming</label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="design" 
                        name="technicalSkills"
                        value="design"
                        checked={formData.technicalSkills.includes('design')}
                        onChange={handleInputChange}
                        className="mr-2" 
                      />
                      <label htmlFor="design" className="text-sm text-slate-700">System Design</label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="project" 
                        name="technicalSkills"
                        value="project"
                        checked={formData.technicalSkills.includes('project')}
                        onChange={handleInputChange}
                        className="mr-2" 
                      />
                      <label htmlFor="project" className="text-sm text-slate-700">Project Management</label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="testing" 
                        name="technicalSkills"
                        value="testing"
                        checked={formData.technicalSkills.includes('testing')}
                        onChange={handleInputChange}
                        className="mr-2" 
                      />
                      <label htmlFor="testing" className="text-sm text-slate-700">Testing & Quality</label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="documentation" 
                        name="technicalSkills"
                        value="documentation"
                        checked={formData.technicalSkills.includes('documentation')}
                        onChange={handleInputChange}
                        className="mr-2" 
                      />
                      <label htmlFor="documentation" className="text-sm text-slate-700">Documentation</label>
                    </div>
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="other-skills" 
                        name="technicalSkills"
                        value="other-skills"
                        checked={formData.technicalSkills.includes('other-skills')}
                        onChange={handleInputChange}
                        className="mr-2" 
                      />
                      <label htmlFor="other-skills" className="text-sm text-slate-700">Other</label>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Resume/CV</label>
                  <div className="mt-1 flex justify-center px-4 py-3 border border-gray-200 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <FileText className="mx-auto h-8 w-8 text-slate-400" />
                      <div className="flex text-sm text-slate-600">
                        <label className="relative cursor-pointer rounded-md font-medium text-cyan-600 hover:text-cyan-500">
                          <span>Upload a file</span>
                          <input 
                            id="file-upload" 
                            name="resume" 
                            type="file" 
                            className="sr-only"
                            accept=".pdf,.doc,.docx"
                            onChange={handleInputChange}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-slate-500">PDF, DOC, DOCX (max 10MB)</p>
                      {formData.resume && (
                        <p className="text-xs text-green-600 font-medium">
                          Selected: {formData.resume.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tell us about yourself</label>
                  <textarea 
                    name="aboutYourself"
                    value={formData.aboutYourself}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                    rows="3"
                    placeholder="Your skills, experience, and why you'd like to join our team"
                  ></textarea>
                </div>
                
                <div className="text-center pt-2">
                  <button 
                    type="submit"
                    disabled={formStatus.isSubmitting}
                    className={`inline-flex items-center px-5 py-2 rounded-md transition-all duration-300 text-sm font-medium ${
                      formStatus.isSubmitting 
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-md'
                    }`}
                  >
                    {formStatus.isSubmitting ? (
                      <>
                        <Loader className="animate-spin mr-1.5 w-4 h-4" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <ArrowRight className="ml-1.5 w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
            
            <div className="mt-6 bg-white rounded-md shadow-md p-5">
              <h3 className="text-base font-bold text-slate-900 mb-3">Our Hiring Process</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-start">
                  <div className="bg-blue-50 w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-blue-600 mr-2 text-xs">1</div>
                  <div>
                    <h4 className="font-medium text-slate-900 text-xs">Application Review</h4>
                    <p className="text-slate-600 text-xs">Within 2 weeks</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-blue-50 w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-blue-600 mr-2 text-xs">2</div>
                  <div>
                    <h4 className="font-medium text-slate-900 text-xs">Interview Process</h4>
                    <p className="text-slate-600 text-xs">Meet our team</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-blue-50 w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-blue-600 mr-2 text-xs">3</div>
                  <div>
                    <h4 className="font-medium text-slate-900 text-xs">Offer & Onboarding</h4>
                    <p className="text-slate-600 text-xs">Join our team</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* FAQ Section - Updated with balanced language */}
      <section className="py-16 bg-white" ref={faqRef}>
        <Container>
          <SectionHeading
            eyebrow="FAQs"
            title={<>Common <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Questions</span></>}
            description="Quick answers about careers at Galo Industries"
            centered
          />
          
          <div className="max-w-2xl mx-auto mt-8 faq-content">
            <div className="bg-white rounded-md shadow-md overflow-hidden">
              <div className="divide-y divide-gray-100">
                {faqItems.map((item, index) => (
                  <div key={index} className="faq-item">
                    <button
                      className="w-full px-4 py-3 flex items-center justify-between text-left"
                      onClick={() => toggleFaq(index)}
                    >
                      <span className="font-medium text-slate-900 text-sm">{item.question}</span>
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${activeFaq === index ? 'rotate-180' : ''}`} />
                    </button>
                    <div 
                      className={`px-4 pb-3 overflow-hidden transition-all duration-300 ${
                        activeFaq === index ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <p className="text-slate-600 text-xs">{item.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="text-center mt-5">
              {/* FAQ section button */}
              <a 
                href="/contact"
                className="inline-flex items-center bg-transparent border border-cyan-500 text-cyan-600 hover:bg-cyan-50 px-4 py-2 rounded-md text-sm transition-all duration-300"
              >
                Contact Us
                <ArrowRight className="ml-1 w-4 h-4" />
              </a>
            </div>
          </div>
        </Container>
      </section>
      
      {/* CTA Banner - Updated with balanced language */}
      <section className="py-12 bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-3">Ready to join our team?</h2>
            <p className="mb-6 text-blue-100 text-sm">Take the next step and discover exciting opportunities at Galo Industries.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <a 
                href="#open-application"
                className="inline-flex items-center bg-white text-blue-600 px-5 py-2 rounded-md hover:bg-blue-50 transition-all duration-300 text-sm font-medium"
              >
                Apply Now
                <ArrowRight className="ml-1.5 w-4 h-4" />
              </a>
              <a 
                href="/about"
                className="inline-flex items-center bg-transparent border border-white text-white rounded-md hover:bg-white/10 transition-all duration-300 text-sm font-medium px-5 py-2"
              >
                Learn More About Us
              </a>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Footer - using imported component */}
      <Footer />
      
      {/* Back to top button with consistent styling */}
      {scrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-md shadow-md flex items-center justify-center hover:-translate-y-1 transition-all duration-300 z-20"
          aria-label="Back to top"
        >
          <ArrowUp className="w-4 h-4" />
        </button>
      )}
      
      <style jsx>{`
        /* Animation for sections */
        .culture-content,
        .benefits-grid,
        .application-content,
        .testimonials-grid,
        .faq-content {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        
        .is-visible .culture-content,
        .is-visible .benefits-grid,
        .is-visible .application-content,
        .is-visible .testimonials-grid,
        .is-visible .faq-content {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Card animations */
        .benefit-card,
        .testimonial-card {
          opacity: 0;
          transform: translateY(15px);
          animation: fadeInUp 0.4s forwards;
          animation-delay: var(--delay, 0ms);
        }
        
        .is-visible .benefit-card,
        .is-visible .testimonial-card {
          animation-name: fadeInUp;
        }
        
        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default GaloCareersPage;